import { OutlinedTextFieldProps } from '@mui/material';
import * as _ from 'lodash';
import * as changeCase from 'change-case';
import {
  AddressPropMap,
  AddressFormConfig,
  AddressFieldAuxProps,
  AddressFormConfigProp,
  UserProfileDefaultAddress,
  addressFormFieldTypes,
  AddressFormConfigOrderedProps,
  AddressModel,
  SelectOption,
  RadioItem,
  FormField,
  UserDefaultBillingAddress,
  UserDefaultAddress,
  CampaignContactBillingAddress,
  CampaignContactAddress,
} from 'types';

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const countries: SelectOption[] = [{ label: 'Schweiz', value: 'Schweiz' }];

export const salutationOptions: RadioItem[] = [
  { label: 'Frau', value: 'female' },
  { label: 'Herr', value: 'male' },
  { label: 'Keine Angabe', value: 'none' },
];

const commonTextFieldProps: OutlinedTextFieldProps = {
  fullWidth: true,
  variant: 'outlined',
  size: 'small',
};

const commonRequiredControlOption = {
  required: 'Fülle bitte das Feld aus',
};

export const addressFormConfigProps: AddressFormConfigOrderedProps = [
  'salutation',
  'firstName',
  'lastName',
  'companyName',
  'addressLine1',
  'addressLine2',
  'country',
  'postcode',
  'city',
  'email',
  'phone',
];

export const addressFieldAuxProps: AddressFieldAuxProps = {
  salutation: {
    defaultValue: 'none',
    radios: salutationOptions,
  },
  firstName: {
    label: 'Vorname',
    registerOptions: commonRequiredControlOption,
    textFieldProps: commonTextFieldProps,
  },
  lastName: {
    label: 'Nachname',
    registerOptions: commonRequiredControlOption,
    textFieldProps: commonTextFieldProps,
  },
  companyName: {
    label: 'Name der Firma',
    registerOptions: commonRequiredControlOption,
    textFieldProps: commonTextFieldProps,
  },
  addressLine1: {
    label: 'Strassenname und Nr.',
    registerOptions: commonRequiredControlOption,
    textFieldProps: commonTextFieldProps,
  },
  addressLine2: {
    label: 'Adresszusatz (Optional)',
    textFieldProps: commonTextFieldProps,
    helperText: 'Beispiel: c/o, Postfach, Gebäude',
  },
  country: {
    label: 'Land',
    options: countries,
  },
  postcode: {
    label: 'PLZ',
    registerOptions: {
      ...commonRequiredControlOption,
      pattern: { value: /^\d{4}$/, message: 'PLZ sollte 4-stellig sein' },
    },
    textFieldProps: commonTextFieldProps,
  },
  city: {
    label: 'Ort',
    registerOptions: commonRequiredControlOption,
    textFieldProps: commonTextFieldProps,
  },
  email: {
    label: 'E-Mail',
    registerOptions: {
      ...commonRequiredControlOption,
      pattern: { value: emailRegex, message: 'Gib bitte eine gültige E-Mail-Adresse an' },
      setValueAs: (v: string) => v.toLowerCase(),
    },
    textFieldProps: commonTextFieldProps,
    helperText: 'An diese E-Mail-Adresse wird die Rechnung gesendet und Updates zur Kampagne.',
  },
  phone: {
    label: 'Telefonnummer',
    registerOptions: {
      ...commonRequiredControlOption,
      pattern: {
        value: phoneRegex,
        message: 'Telefon ist nicht gültig',
      },
    },
    helperText: 'Nur für den Fall wenn Fragen auftauchen. Die Nummer wird nicht weitergegeben.',
  },
};

export const billingAddressFieldAuxProps = _.merge(
  {},
  addressFieldAuxProps,
  Object.fromEntries(addressFormConfigProps.map((prop) => [prop, { registerOptions: { shouldUnregister: true } }]))
) as AddressFieldAuxProps;

export const getFieldName = <FieldPrefix extends string>(prop: AddressFormConfigProp, fieldPrefix: FieldPrefix) =>
  `${fieldPrefix}${changeCase.pascalCase(String(prop).replace(/^Address/i, ''))}`;

const getAddressFormConfig = <Prefix extends string, Model extends AddressModel<Prefix>>(
  fieldPrefix: Prefix,
  fieldAuxProps: AddressFieldAuxProps
) =>
  addressFormConfigProps.reduce((acc, prop) => {
    (acc as AddressPropMap<FormField>)[prop] = {
      ...fieldAuxProps[prop],
      fieldType: addressFormFieldTypes[prop],
      field: getFieldName(prop, fieldPrefix),
    } as FormField;
    return acc;
  }, {} as AddressFormConfig<Model>);

export const campaignContactAddressFormConfig = getAddressFormConfig(
  'contactAddress',
  addressFieldAuxProps
) as AddressFormConfig<CampaignContactAddress>;
export const campaignBillingAddressFormConfig = getAddressFormConfig(
  'billingAddress',
  billingAddressFieldAuxProps
) as AddressFormConfig<CampaignContactBillingAddress>;
export const userProfileDefaultAddressFormConfig = getAddressFormConfig(
  'defaultContactAddress',
  addressFieldAuxProps
) as AddressFormConfig<UserDefaultAddress>;
export const userProfileDefaultContactBillingFormConfig = getAddressFormConfig(
  'defaultBillingAddress',
  billingAddressFieldAuxProps
) as AddressFormConfig<UserDefaultBillingAddress>;

export const defaultUserProfileAddressFormValues: UserProfileDefaultAddress = {
  defaultBillingAddressSalutation: 'none',
  defaultBillingAddressFirstName: '',
  defaultBillingAddressLastName: '',
  defaultBillingAddressLine1: '',
  defaultBillingAddressLine2: '',
  defaultBillingAddressCountry: 'Schweiz',
  defaultBillingAddressPostcode: '',
  defaultBillingAddressCity: '',
  defaultBillingAddressEmail: '',
  defaultBillingAddressPhone: '',
  defaultBillingAddressCompanyName: '',

  defaultContactAddressSalutation: 'none',
  defaultContactAddressFirstName: '',
  defaultContactAddressLastName: '',
  defaultContactAddressLine1: '',
  defaultContactAddressLine2: '',
  defaultContactAddressCountry: 'Schweiz',
  defaultContactAddressPostcode: '',
  defaultContactAddressCity: '',
  defaultContactAddressEmail: '',
  defaultContactAddressPhone: '',
  defaultContactAddressCompanyName: '',
};
